import React, {useEffect, useMemo, useState} from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {getAdvertisers} from "../../../../api/advertiserApi";
import {getAgencies} from "../../../../api/agencyApi";
import {getAdNetworks} from "../../../../api/adNetworkApi";
import * as Yup from "yup";
import {useFormik} from "formik";
import { confirmImportCampaign, importCampaign } from '../../../../api/campaignApi';
import TableContainer from "../../../../components/TableContainerReactTable";
import { format } from 'date-fns';
import { Advertiser } from '../../../../types/advertiser';
import { Agency } from '../../../../types/agency';
import { Campaign } from '../../../../types/campaign';
import { AdNetwork } from '../../../../types/ad-network';
import { toast } from 'react-toastify';
import { BlockedKeywords } from '../../../../types/blocked-keywords';
import { getBlockedKeywords } from '../../../../api/blockedKeywordsApi';
import { Loader2 } from 'lucide-react';

interface AddModalProps {
    isOpen: boolean;
    toggle: () => void;
}

const ImportCampaignModal = ({ isOpen, toggle }: AddModalProps) => {
    const [agencies, setAgencies] = useState<Agency[]>([]);
    const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
    const [adNetworks, setAdNetworks] = useState<AdNetwork[]>([]);
    const [palavrasChaveProtegida, setPalavrasChaveProtegida] = useState<BlockedKeywords[]>([]);
    const [activeArrowTab, setActiveArrowTab] = useState<number>(1);
    const [firstStepImportButtonLoader, setFirstStepImportButtonLoader] = useState<boolean>(false);
    const [secondStepImportButtonLoader, setSecondStepImportButtonLoader] = useState<boolean>(false);
    const [campaignToSave, setCampaignToSave] = useState<Campaign|null>(null);

    useEffect(() => {
        // Pega os advertisers, agencies, ad networks e blocked keywords para popular os <select>
        getAdvertisers().then(setAdvertisers);
        getAgencies().then(setAgencies);
        getAdNetworks().then(setAdNetworks);
        getBlockedKeywords().then(setPalavrasChaveProtegida);
    }, []);

    const toggleImportCampaignModal = () => {
        toggle();
        setActiveArrowTab(1);
        handleFirstStepForm.resetForm();
        setCampaignToSave(null);
    };

    const ordersToAddColumns = useMemo(
        () => [
            {
                header: "Midia Cad",
                cell: (cell: any) => {
                    const mediaCad = cell.getValue();
                    if (mediaCad) {
                        return <span className="fw-semibold">{cell.getValue()}</span>;
                    }
                    return <span className="badge bg-danger">não encontrado</span>;
                },
                accessorFn: (row: any) => row.publisher ? row.publisher.mediaCad : '',
                enableColumnFilter: false,
            },
            {
                header: "Data Início",
                accessorFn: (row: any) => {
                    return row.startDate ? format(row.startDate, 'dd/MM/yyyy') : '';
                },
                enableColumnFilter: false,

            },
            {
                header: "Data Termino",
                accessorFn: (row: any) => {
                    return row.endDate ? format(row.endDate, 'dd/MM/yyyy') : '';
                },
                enableColumnFilter: false,
            },
            {
                header: "Publisher",
                accessorFn: (row: any) => row.publisher ? row.publisher.name : '',
                cell: (cell: any) => {
                    return cell.getValue() || <span className="badge bg-danger">não encontrado</span>;
                },
                enableColumnFilter: false,
            },
            {
                header: "Placement",
                accessorFn: (row: any) => row.placement ? row.placement.name : '',
                cell: (cell: any) => {
                    return cell.getValue() || <span className="badge bg-danger">não encontrado</span>;
                },
                enableColumnFilter: false,
            },
            {
                header: 'Tipo',
                accessorKey: 'adType',
                cell: (cell: any) => {
                    const adType = cell.getValue().toLowerCase();
                    const badgeColor = (adType === 'display') ? 'primary' : (adType === 'video_audio' ? 'warning': 'danger');
                    return <span className={`badge bg-${badgeColor}`}>{adType.replace('_', '/') || 'não encontrado'}</span>;
                },
                enableColumnFilter: false,
            },
            {
                header: "Segmentação",
                accessorKey: "segmentation",
                enableColumnFilter: false,
            },
            {
                header: "Linha criativa",
                accessorKey: "creativeLine",
                enableColumnFilter: false,
            },
            {
                header: "Visibilidade",
                accessorKey: "viewability",
                cell: (cell: any) => {
                    const row = cell.row.original;
                    return <span>&gt; {row.viewability}%</span>;
                },
                enableColumnFilter: false,
            },
            {
                header: 'Tamanhos',
                accessorKey: 'sizes',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    const actionCell: any[] = [];
                    row.sizes?.forEach((size: string) => {
                        actionCell.push(<span
                            className="badge rounded-pill border border-primary text-primary">{size}</span>);
                    });
                    return <div className="d-flex gap-2">{actionCell}</div>;
                },
                enableSorting: true,
                enableColumnFilter: false,
            },
            {
                header: "Tipo de compra",
                accessorKey: "pricingModel",
                enableColumnFilter: false,
            },
            {
                header: "Impressões",
                accessorFn: (row: any) => {
                    return row.impressions ? new Intl.NumberFormat('pt-BR').format(row.impressions) : '-';
                },
                enableColumnFilter: false,
            },
            {
                header: 'Erros e Avisos',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    const errors = row.errors.length === 0 ? '' : row.errors.map((error: string) => (
                        <span className="badge rounded-pill border border-danger text-danger">{error}</span>
                    ));
                    const warnings = row.warnings.length === 0 ? '' : row.warnings.map((warning: string) => (
                        <span className="badge rounded-pill border border-warning text-warning">{warning}</span>
                    ));
                    return (<>{errors}{warnings}</>);
                },
                enableSorting: true,
                enableColumnFilter: false,
            },

        ],
        []
    );

    // const SUPPORTED_FORMATS = [
    //     "text/csv",
    //     "text/comma-separated-values",
    // ];

    // First Step Form Validation
    const handleFirstStepForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            name: undefined,
            advertiserId: undefined,
            agencyId: undefined,
            adNetworkId: undefined,
            blockedKeywords: [],
            file: undefined,
        },

        validationSchema: Yup.object({
            name: Yup.string().required("Nome da campaign é obrigatório"),
            advertiserId: Yup.number().optional(),
            agencyId: Yup.number().optional(),
            adNetworkId: Yup.number().optional(),
            blockedKeywords: Yup.array(),
            file: Yup.mixed().optional()
            // TODO: As validações abaixo não estão funcionando
            // .test(
            //     "fileFormat",
            //     "Placement de arquivo inválido",
            //     (value: any) => value && SUPPORTED_FORMATS.includes(value.type)
            // )
            // .test(
            //     "fileSize",
            //     "O arquivo é muito grande",
            //     (value: any) => value && value.size <= 2000000
            // ),
        }),
        onSubmit: (values: any) => {
            setFirstStepImportButtonLoader(true);

            const formData = new FormData();
            formData.append('name', values.name);
            
            // Only append optional fields if they have values
            if (values.advertiserId) {
                formData.append('advertiserId', values.advertiserId);
            }
            if (values.agencyId) {
                formData.append('agencyId', values.agencyId);
            }
            if (values.adNetworkId) {
                formData.append('adNetworkId', values.adNetworkId);
            }
            
            const selectedOptions = (document.getElementById('palavras-chave-protegidas-field') as HTMLSelectElement).selectedOptions;
            const palavrasChaveProtegidaIds = Array.from(selectedOptions).map(({ value }) => value);
            if (palavrasChaveProtegidaIds.length > 0) {
                palavrasChaveProtegidaIds.forEach((item: any) => formData.append('blockedKeywords[]', item));
            }

            const file: HTMLInputElement | null = document.querySelector('#file-field');
            if (file && file.files && file.files[0]) {
                formData.append('file', file.files[0]);
            }

            return importCampaign(formData)
                .then((res) => {
                    setCampaignToSave(res);
                    setActiveArrowTab(2);
                })
                .catch(() => {})
                .finally(() => {
                    setFirstStepImportButtonLoader(false);
                });
        },
    });

    // First Step Form Validation
    const handleSecondStepForm = (event: any) => {
        event.preventDefault();

        const campaignsToSaveLenght = campaignToSave?.orders?.length || 0;
        
        if (campaignsToSaveLenght > 0) {
            if (!window.confirm(`Você tem certeza que deseja importar ${campaignsToSaveLenght} pedidos?`)) {
                return;
            }

            const countWarnings = campaignToSave?.orders.filter((order: any) => order.warnings.length > 0).length || 0;
            if (countWarnings && !window.confirm(`Existem ${countWarnings} pedidos com avisos, deseja continuar?`)) {
                return;
            }

            const countErrors = campaignToSave?.orders.filter((order: any) => order.errors.length > 0).length || 0;
            if (countErrors) {
                window.alert(`Existem ${countErrors} pedidos com erros, não é possível continuar.`)
                return;
            }
        } else {
            if (!window.confirm(`Você tem certeza que deseja criar a campanha sem pedidos?`)) {
                return;
            }
        }

        setSecondStepImportButtonLoader(true);
        
        confirmImportCampaign(campaignToSave).then((res: any) => {
            toggleImportCampaignModal();
            toast.success('Campanha criada com sucesso!');
        })
            .catch(() => {
            })
            .finally(() => {
                setSecondStepImportButtonLoader(false);
            });
    };

    return (
        <Dialog open={isOpen} onOpenChange={toggleImportCampaignModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Adicionar campanha</DialogTitle>
                    <DialogDescription>
                        Importe uma campanha de um arquivo CSV.
                    </DialogDescription>
                </DialogHeader>

                <Tabs value={activeArrowTab.toString()} onValueChange={(value) => setActiveArrowTab(Number(value))}>
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="1" disabled={activeArrowTab < 1 && activeArrowTab > 1}>
                            Campanha
                        </TabsTrigger>
                        <TabsTrigger value="2" disabled={!campaignToSave}>
                            Confirme
                        </TabsTrigger>
                        <TabsTrigger value="3" disabled>
                            Concluído
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="1">
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFirstStepForm.handleSubmit();
                            return false;
                        }}>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="nome-field">Nome</Label>
                                    <Input
                                        type="text"
                                        id="nome-field"
                                        name="name"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.name}
                                    />
                                    {handleFirstStepForm.touched.name && handleFirstStepForm.errors.name && (
                                        <p className="text-sm text-red-500">{handleFirstStepForm.errors.name}</p>
                                    )}
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="advertiser-id-field">Advertiser</Label>
                                    <Input
                                        type="select"
                                        id="advertiser-id-field"
                                        name="advertiserId"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.advertiserId}
                                    >
                                        <option value="">Selecione um advertiser</option>
                                        {advertisers && advertisers.map((advertiser: any) => (
                                            <option key={advertiser.id} value={advertiser.id}>
                                                {advertiser.name}
                                            </option>
                                        ))}
                                    </Input>
                                    {handleFirstStepForm.touched.advertiserId && handleFirstStepForm.errors.advertiserId && (
                                        <p className="text-sm text-red-500">{handleFirstStepForm.errors.advertiserId}</p>
                                    )}
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="agency-id-field">Agência</Label>
                                    <Input
                                        type="select"
                                        id="agency-id-field"
                                        name="agencyId"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.agencyId}
                                    >
                                        <option value="">Selecione uma agência</option>
                                        {agencies && agencies.map((agency: any) => (
                                            <option key={agency.id} value={agency.id}>
                                                {agency.name}
                                            </option>
                                        ))}
                                    </Input>
                                    {handleFirstStepForm.touched.agencyId && handleFirstStepForm.errors.agencyId && (
                                        <p className="text-sm text-red-500">{handleFirstStepForm.errors.agencyId}</p>
                                    )}
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="ad-network-id-field">Ad Network</Label>
                                    <Input
                                        type="select"
                                        id="ad-network-id-field"
                                        name="adNetworkId"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.adNetworkId}
                                    >
                                        <option value="">Selecione uma ad network</option>
                                        {adNetworks && adNetworks.map((adNetwork: any) => (
                                            <option key={adNetwork.id} value={adNetwork.id}>
                                                {adNetwork.name}
                                            </option>
                                        ))}
                                    </Input>
                                    {handleFirstStepForm.touched.adNetworkId && handleFirstStepForm.errors.adNetworkId && (
                                        <p className="text-sm text-red-500">{handleFirstStepForm.errors.adNetworkId}</p>
                                    )}
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="palavras-chave-protegidas-field">Proteções: Lista de palavras</Label>
                                    <Input
                                        type="select"
                                        multiple
                                        id="palavras-chave-protegidas-field"
                                        name="blockedKeywords"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.blockedKeywords}
                                    >
                                        {palavrasChaveProtegida && palavrasChaveProtegida.map((palavraChave) => (
                                            <option key={palavraChave.id} value={palavraChave.id}>
                                                {palavraChave.name}
                                            </option>
                                        ))}
                                    </Input>
                                    {handleFirstStepForm.touched.blockedKeywords && handleFirstStepForm.errors.blockedKeywords && (
                                        <p className="text-sm text-red-500">{handleFirstStepForm.errors.blockedKeywords}</p>
                                    )}
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="file-field">Pedidos</Label>
                                    <Input
                                        type="file"
                                        id="file-field"
                                        name="file"
                                        accept=".csv"
                                        onChange={handleFirstStepForm.handleChange}
                                        onBlur={handleFirstStepForm.handleBlur}
                                        value={handleFirstStepForm.values.file}
                                    />
                                    {handleFirstStepForm.touched.file && handleFirstStepForm.errors.file && (
                                        <p className="text-sm text-red-500">{handleFirstStepForm.errors.file}</p>
                                    )}
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={firstStepImportButtonLoader}>
                                    {firstStepImportButtonLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Importar
                                </Button>
                            </DialogFooter>
                        </form>
                    </TabsContent>

                    <TabsContent value="2">
                        <div className="grid gap-4 py-4">
                            <div>
                                <p>Nome da campanha: {campaignToSave?.name}</p>
                                {campaignToSave?.advertiser && <p>Nome do advertiser: {campaignToSave.advertiser.name}</p>}
                                {campaignToSave?.agency && <p>Nome da agência: {campaignToSave.agency.name}</p>}
                                {campaignToSave?.adNetwork && <p>Nome da ad network: {campaignToSave.adNetwork.name}</p>}
                            </div>
                            {campaignToSave?.orders && campaignToSave.orders.length > 0 ? (
                                <div>
                                    <TableContainer
                                        divClass="table-responsive"
                                        columns={(ordersToAddColumns || [])}
                                        data={(campaignToSave?.orders || [])}
                                        isGlobalFilter={true}
                                        customPageSize={30}
                                        SearchPlaceholder="Procurar..."
                                    />
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 p-4 bg-blue-50 text-blue-700 rounded">
                                    <i className="ri-information-line"></i>
                                    <p>Nenhum pedido foi importado. A campanha será criada sem pedidos.</p>
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setActiveArrowTab(activeArrowTab - 1);
                                    setCampaignToSave(null);
                                }}
                            >
                                Cancelar & Voltar
                            </Button>
                            <Button
                                type="button"
                                disabled={secondStepImportButtonLoader}
                                onClick={handleSecondStepForm}
                            >
                                {secondStepImportButtonLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Confirmar
                            </Button>
                        </DialogFooter>
                    </TabsContent>

                    <TabsContent value="3">
                        <div className="flex flex-col items-center justify-center py-8">
                            <div className="flex items-center justify-center w-20 h-20 bg-green-100 text-green-600 rounded-full mb-4">
                                <i className="ri-checkbox-circle-fill text-4xl"></i>
                            </div>
                            <h5 className="text-xl font-semibold mb-2">Muito bom!</h5>
                            <p className="text-muted text-center">
                                Suas campaigns foram cadastradas com sucesso
                            </p>
                            <Button onClick={toggleImportCampaignModal} className="mt-4">
                                Fechar
                            </Button>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};

export default ImportCampaignModal;